import axios from 'axios';
import { API_URL } from '@/lib/api';

/**
 * THE SUPERADMIN'S CONSOLE SIGN-IN, SEEN FROM INSIDE A WORKSPACE (owner, 25 Oct
 * 2026: "why cant the superadmin switch from one workspace to another just
 * from the dropdown?").
 *
 * A superadmin in the app holds a 2-hour tenant token the console minted for
 * one workspace (imp: true); the console's own sign-in stays in this browser
 * as platform_token. Switching from the workspace menu asks the console's own
 * endpoints with it: the list the console shows, and POST
 * /api/platform/orgs/:id/impersonate, which is owner-only and logged as
 * impersonation_started. So a switch here is exactly the console's "Open
 * workspace", without the trip back to the console. Nothing new is trusted.
 *
 * Its own small client, not the console's (pages/superadmin/shared), so the
 * workspace bundle never pulls in console screens.
 */
const client = axios.create({ baseURL: API_URL, timeout: 20_000 });
client.interceptors.request.use((config) => {
  const token = platformToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function platformToken() {
  try { return localStorage.getItem('platform_token'); } catch { return null; }
}

/** Every workspace the console lists, as the menu's rows. */
export async function fetchPlatformWorkspaces() {
  const { data } = await client.get('/api/platform/orgs');
  return (Array.isArray(data) ? data : [])
    .map((o) => ({ org_id: o.id, name: o.name, status: o.status }))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
}

/**
 * Open another workspace as its admin, the console's way, in this tab. The
 * page reloads into it, so nothing from the last workspace stays cached.
 */
export async function switchAsSuperadmin(orgId) {
  const { data } = await client.post(`/api/platform/orgs/${orgId}/impersonate`);
  localStorage.setItem('token', data.tenant_token);
  window.location.assign('/app/dashboard');
}

/** Plain words for why a switch did not happen. */
export function switchFailure(err) {
  const status = err?.response?.status;
  if (status === 401) return 'Your console sign-in has expired. Sign in to the console again to switch.';
  if (status === 403) return 'Only a console owner can open another workspace.';
  return err?.response?.data?.error || 'The workspace could not be opened. Try again.';
}
